import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, CircularProgress, Alert, IconButton,
  Snackbar, Tabs, Tab, Badge,
} from '@mui/material';
import { Add, Edit, Warning } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(+n || 0);

function getDaysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function ExpiryChip({ date }) {
  const days = getDaysUntilExpiry(date);
  if (days === null) return <Typography variant="caption">—</Typography>;
  if (days < 0) return <Chip label="Expired" size="small" color="error" />;
  if (days <= 30) return <Chip label={`${days}d`} size="small" color="warning" icon={<Warning sx={{ fontSize: 14 }} />} />;
  if (days <= 90) return <Chip label={`${days}d`} size="small" color="info" />;
  return <Typography variant="caption">{new Date(date).toLocaleDateString('en-PK')}</Typography>;
}

const EMPTY = { name: '', sku: '', batch_number: '', expiry_date: '', quantity: '', purchase_price: '', sale_price: '', manufacturer: '', unit: 'strip' };

function MedicineDialog({ medicine, open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(medicine ? {
      name: medicine.name || '', sku: medicine.sku || '',
      batch_number: medicine.batch_number || '',
      expiry_date: medicine.expiry_date ? medicine.expiry_date.split('T')[0] : '',
      quantity: medicine.stock || '',
      purchase_price: medicine.purchase_price || '',
      sale_price: medicine.sale_price || '',
      manufacturer: medicine.manufacturer || '',
      unit: medicine.unit || 'strip',
    } : EMPTY);
    setError('');
  }, [medicine, open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handle = async () => {
    if (!form.name) { setError('Medicine name is required.'); return; }
    setLoading(true); setError('');
    try {
      const payload = { ...form, category_name: 'Medicine', notes: form.batch_number ? `Batch: ${form.batch_number}` : '' };
      if (medicine?.id) await api.put(`/products/${medicine.id}`, payload);
      else await api.post('/products', payload);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save medicine');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{medicine?.id ? 'Edit Medicine' : 'Add Medicine'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Medicine Name" value={form.name} onChange={set('name')} fullWidth required />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="SKU / Code" value={form.sku} onChange={set('sku')} fullWidth />
          <TextField label="Manufacturer" value={form.manufacturer} onChange={set('manufacturer')} fullWidth />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Batch Number" value={form.batch_number} onChange={set('batch_number')} fullWidth />
          <TextField label="Expiry Date" type="date" value={form.expiry_date} onChange={set('expiry_date')} fullWidth InputLabelProps={{ shrink: true }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select label="Unit" value={form.unit} onChange={set('unit')} fullWidth>
            {['strip', 'bottle', 'box', 'vial', 'sachet', 'tablet', 'capsule', 'ml', 'mg'].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </TextField>
          <TextField label="Opening Stock" type="number" value={form.quantity} onChange={set('quantity')} fullWidth />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Purchase Price (₨)" type="number" value={form.purchase_price} onChange={set('purchase_price')} fullWidth />
          <TextField label="Sale Price (₨)" type="number" value={form.sale_price} onChange={set('sale_price')} fullWidth />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : medicine?.id ? 'Update' : 'Add Medicine'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MedicalPage() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products', { params: { limit: 200, category: 'Medicine' } });
      setMedicines((data.data || []).map(p => ({
        ...p,
        batch_number: p.notes?.match(/Batch: (.+)/)?.[1] || '',
        expiry_date: p.expiry_date || null,
        stock: p.stock || 0,
      })));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const expiringSoon = medicines.filter(m => { const d = getDaysUntilExpiry(m.expiry_date); return d !== null && d <= 90; });
  const expired = medicines.filter(m => { const d = getDaysUntilExpiry(m.expiry_date); return d !== null && d < 0; });
  const lowStock = medicines.filter(m => m.stock <= 10);

  const displayed = tab === 0 ? medicines : tab === 1 ? expiringSoon : tab === 2 ? expired : lowStock;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Medical Store — Medicine Inventory</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setSelected(null); setDialogOpen(true); }}>Add Medicine</Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All (${medicines.length})`} />
        <Tab label={<Badge badgeContent={expiringSoon.length} color="warning">Expiring Soon</Badge>} />
        <Tab label={<Badge badgeContent={expired.length} color="error">Expired</Badge>} />
        <Tab label={<Badge badgeContent={lowStock.length} color="info">Low Stock</Badge>} />
      </Tabs>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Medicine</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Batch #</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right">Sale Price</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map(m => (
                  <TableRow key={m.id} hover sx={getDaysUntilExpiry(m.expiry_date) < 0 ? { bgcolor: 'error.50' } : {}}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{m.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{m.sku}</Typography>
                    </TableCell>
                    <TableCell>{m.manufacturer || '—'}</TableCell>
                    <TableCell>{m.batch_number || '—'}</TableCell>
                    <TableCell><ExpiryChip date={m.expiry_date} /></TableCell>
                    <TableCell>{m.unit || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600} color={m.stock <= 10 ? 'error.main' : 'inherit'}>{m.stock}</Typography>
                    </TableCell>
                    <TableCell align="right">₨ {fmt(m.sale_price)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelected(m); setDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {displayed.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No medicines found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MedicineDialog
        medicine={selected} open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { setDialogOpen(false); setSnack(selected ? 'Medicine updated' : 'Medicine added'); load(); }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
